import { useState, useEffect, useMemo } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchAllCities, fetchCitiesByState, filterCities, type IBGECity } from "@/lib/ibgeCities";

interface CityAutocompleteProps {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  // Variante para uso no SearchBar (sem bordas, integrado ao card)
  variant?: "default" | "embedded";
  // UF para filtrar cidades (opcional)
  state?: string;
}

export function CityAutocomplete({
  value,
  onChange,
  placeholder = "Buscar cidade...",
  disabled = false,
  className,
  variant = "default",
  state,
}: CityAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [cities, setCities] = useState<IBGECity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedState, setLoadedState] = useState<string | null>(null);

  // Carregar cidades quando o popover abrir ou quando o estado mudar
  useEffect(() => {
    if (open && state !== loadedState) {
      setLoading(true);
      const fetchData = state ? fetchCitiesByState(state) : fetchAllCities();
      fetchData
        .then(data => {
          setCities(data);
          setLoadedState(state || null);
        })
        .finally(() => setLoading(false));
    }
  }, [open, state, loadedState]);

  // Resetar cidades quando o estado mudar
  useEffect(() => {
    if (state !== loadedState) {
      setCities([]);
      setLoadedState(null);
    }
  }, [state, loadedState]);

  // Filtrar cidades baseado na busca
  const filteredCities = useMemo(() => {
    return filterCities(cities, search, 100);
  }, [cities, search]);

  const isEmbedded = variant === "embedded";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "justify-between font-normal",
            isEmbedded && "border-0 bg-transparent shadow-none hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-12 pl-12 text-base",
            !isEmbedded && "w-full",
            !value && "text-muted-foreground",
            className
          )}
        >
          {value || placeholder}
          {!isEmbedded && (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] min-w-[280px] p-0 bg-popover z-50" 
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite para buscar..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6 gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Carregando cidades...</span>
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {filteredCities.map((city) => (
                    <CommandItem
                      key={city.id}
                      value={city.displayName}
                      onSelect={() => {
                        // Retorna apenas o nome da cidade quando tem state, senão displayName
                        onChange(state ? city.nome : city.displayName);
                        setOpen(false);
                        setSearch("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === city.nome || value === city.displayName ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                      {city.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
